/**
 * Roberts Jewelers — Calendly popup.
 *
 * Any element carrying `data-rj-calendly` opens the Calendly scheduling flow in
 * Calendly's own popup instead of navigating. The scheduling URL comes from the
 * attribute value, falling back to the element's href — so triggers stay real
 * links and a blocked script (or no JS at all) still lands the visitor on the
 * Calendly page rather than doing nothing.
 *
 * Calendly's widget.js / widget.css are fetched on the FIRST click only, so the
 * page load stays free of third-party requests.
 *
 * The module also answers the global `booking:open` event that "See the
 * Difference In Person" dispatches (assets/roberts-in-person.js) by opening the
 * first Calendly trigger on the page — that CTA shares the PDP booking flow.
 * When no Calendly trigger is rendered the event falls through to the built-in
 * booking <dialog> in assets/roberts-buy-actions.js, unchanged.
 *
 * Product context reaches the Calendly owner through two channels, because
 * neither is guaranteed on its own:
 *
 *   1. A prefilled custom question (`data-rj-calendly-details` into the slot
 *      named by `data-rj-calendly-answer`, e.g. "a1" = the event type's FIRST
 *      question). This is the channel that shows up on the booking itself — the
 *      confirmation email, the calendar invite and the Calendly event page. It
 *      needs a matching question on the Calendly event type; if there is none,
 *      Calendly ignores the value and nothing breaks.
 *   2. UTM fields (product/variant as utm_content, SKU as utm_term). These are
 *      hidden from the invitee, always accepted, and ride along on the invitee
 *      record — visible in Calendly's scheduled-event details and in webhook /
 *      Zapier / API payloads.
 *
 * Both land in the Calendly account, which is where the owner reads bookings —
 * the storefront sends no mail of its own.
 *
 * Optional data attributes on the trigger:
 *   data-rj-calendly-details — product link for the custom question.
 *   data-rj-calendly-answer  — question slot for that link ("a1"…"a10").
 *   data-rj-calendly-product — product + variant, sent as utm_content.
 *   data-rj-calendly-sku     — variant SKU, sent as utm_term.
 *   data-rj-calendly-name / -email — prefill for logged-in customers.
 */

const WIDGET_CSS = 'https://assets.calendly.com/assets/external/widget.css';
const WIDGET_JS = 'https://assets.calendly.com/assets/external/widget.js';

/** @type {Promise<any> | null} In-flight (or settled) widget load, shared by every trigger. */
let widgetLoad = null;

/** @returns {Promise<any>} Resolves with the global `Calendly` API object. */
function loadWidget() {
  if (window.Calendly) return Promise.resolve(window.Calendly);
  if (widgetLoad) return widgetLoad;

  widgetLoad = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${WIDGET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = WIDGET_CSS;
      document.head.append(link);
    }

    // An inline Calendly embed elsewhere on the page may already have added the script;
    // reuse that tag (window.Calendly above covers the case where it has already run).
    let script = document.querySelector(`script[src="${WIDGET_JS}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = WIDGET_JS;
      script.async = true;
      document.head.append(script);
    }

    script.addEventListener(
      'load',
      () => (window.Calendly ? resolve(window.Calendly) : reject(new Error('Calendly widget exposed no API'))),
      { once: true }
    );
    script.addEventListener('error', () => reject(new Error('Calendly widget failed to load')), { once: true });
  });

  return widgetLoad;
}

/** @param {Element} trigger */
function openPopup(trigger) {
  const url = trigger.getAttribute('data-rj-calendly') || trigger.getAttribute('href');
  if (!url) return;

  /** @type {Record<string, any>} */
  const options = { url };

  if (trigger instanceof HTMLElement) {
    const {
      rjCalendlyProduct: product,
      rjCalendlySku: sku,
      rjCalendlyDetails: details,
      rjCalendlyAnswer: answer,
      rjCalendlyName: name,
      rjCalendlyEmail: email,
    } = trigger.dataset;

    if (product || sku) options.utm = { ...(product && { utmContent: product }), ...(sku && { utmTerm: sku }) };

    // `customAnswers` keys are positional ("a1" = first question on the event type), so the
    // slot is a theme setting rather than a hard-coded 1 — the merchant may already use
    // question 1 for something else.
    const prefill = {
      ...(name && { name }),
      ...(email && { email }),
      ...(details && answer && { customAnswers: { [answer]: details } }),
    };
    if (Object.keys(prefill).length) options.prefill = prefill;
  }

  loadWidget()
    .then((Calendly) => Calendly.initPopupWidget(options))
    .catch(() => {
      widgetLoad = null; // let a later click retry the load
      window.open(url, '_blank', 'noopener');
    });
}

// Delegated on document so it survives Theme Editor re-renders and covers every instance.
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const trigger = target?.closest('[data-rj-calendly]');
  if (!trigger) return;
  event.preventDefault();
  openPopup(trigger);
});

document.addEventListener('booking:open', () => {
  const trigger = document.querySelector('[data-rj-calendly]');
  if (trigger) openPopup(trigger);
});
